import { Injectable } from '@angular/core';
import { PropertyStateService } from '../state/property-state.service';
import { CalculatorService } from './calculator.service';
import { CurrencyPipe } from '@angular/common';

@Injectable()
export class OfferRentalCalculatorService {
    // Internal storage of Inputs. The equivalently named public setters exist and should be set by the user.
    #monthlyRent: number = 0;
    #desiredGrossROIPercent: number = 0;
    #closingCostPercent: number = 0;
    #estimatedRenovationCost: number = 0;
    #managementFeePercent: number = 0;
    #commissionForSellingAgentPercent: number = 0;
    #wholeSaleMarkup: number = 0;
    #arv: number = 0;

    // Outputs (outside of the class they should be treated as readonly)
    #annualGrossIncome: number = 0;
    #estimatedClosingCosts: number = 0;
    #buyerAcquisitionCost: number = 0;
    #managementFee: number = 0;
    #frontSideCommissionEarned: number = 0;
    #totalSBSpread: number = 0;
    #secondSideSalePrice: number = 0;
    #offerPrice: number = 0;

    public insuranceEstimateAnnual: number = 0;

    constructor(
        private state: PropertyStateService,
        private calculator: CalculatorService,
        private currencyPipe: CurrencyPipe
    ) {}

    /**
     * Input Setters
     * Each setter will update the output values which depend on it (i.e are derived by it)
     */

    set arv(value: number) {
        this.#arv = value;
        // Update derived Outputs
        this.insuranceEstimateAnnual = (this.arv * 0.00035) * 12;
    }

    get arv() {
        return this.#arv;
    }

    set monthlyRent(value: number) {
        this.#monthlyRent = value;
        // Update derived Outputs
        this.annualGrossIncome = this.calcAnnualGrossIncome(this.monthlyRent);
    }

    get monthlyRent() {
        return this.#monthlyRent as number;
    }

    set desiredGrossROIPercent(value: number) {
        this.#desiredGrossROIPercent = value;

        // Update derived Outputs
        this.estimatedClosingCosts = this.calcEstimatedClosingCosts(this.annualGrossIncome, this.desiredGrossROIPercent, this.closingCostPercent);
        this.secondSideSalePrice = this.calcSecondSideSalePrice(this.annualGrossIncome, this.desiredGrossROIPercent, this.buyerAcquisitionCost);
    }

    get desiredGrossROIPercent() {
        return this.#desiredGrossROIPercent;
    }

    set closingCostPercent(value: number) {
        this.#closingCostPercent = value;

        // Update derived Outputs
        this.estimatedClosingCosts = this.calcEstimatedClosingCosts(this.annualGrossIncome, this.desiredGrossROIPercent, this.closingCostPercent);
    }

    get closingCostPercent() {
        return this.#closingCostPercent;
    }

    set estimatedRenovationCost(value: number) {
        this.#estimatedRenovationCost = value;

        // Update derived Outputs
        this.buyerAcquisitionCost = this.calcBuyerAcquisitionCost(this.estimatedRenovationCost, this.estimatedClosingCosts);
    }

    get estimatedRenovationCost() {
        return this.#estimatedRenovationCost;
    }

    set managementFeePercent(value: number) {
        this.#managementFeePercent = value;

        // Update derived Outputs
        this.managementFee = this.calcManagementFee(this.annualGrossIncome, this.managementFeePercent);
    }

    get managementFeePercent() {
        return this.#managementFeePercent;
    }

    set commissionForSellingAgentPercent(value: number) {
        this.#commissionForSellingAgentPercent = value;

        // Update derived Outputs
        this.frontSideCommissionEarned = this.calcFrontSideCommissionEarned(this.commissionForSellingAgentPercent, this.offerPrice);
    }

    get commissionForSellingAgentPercent() {
        return this.#commissionForSellingAgentPercent;
    }

    set wholeSaleMarkup(value: number) {
        this.#wholeSaleMarkup = value;

        // Update derived Outputs
        this.totalSBSpread = this.calcTotalSBSpread(this.wholeSaleMarkup, this.frontSideCommissionEarned);
        this.offerPrice = this.calcOfferPrice(this.secondSideSalePrice, this.wholeSaleMarkup);
    }

    get wholeSaleMarkup() {
        return this.#wholeSaleMarkup;
    }

    /**
     * Output Setters
     * Each setter will update the other output values which depend on it (i.e are derived by it).
     */
    set annualGrossIncome(value: number) {
        this.#annualGrossIncome = value;

        // Update derived Outputs
        this.estimatedClosingCosts = this.calcEstimatedClosingCosts(this.annualGrossIncome, this.desiredGrossROIPercent, this.closingCostPercent);
        this.managementFee = this.calcManagementFee(this.annualGrossIncome, this.managementFeePercent);
        this.secondSideSalePrice = this.calcSecondSideSalePrice(this.annualGrossIncome, this.desiredGrossROIPercent, this.buyerAcquisitionCost);
    }

    get annualGrossIncome() {
        return this.#annualGrossIncome;
    }

    set estimatedClosingCosts(value: number) {
        this.#estimatedClosingCosts = value;

        // Update derived Outputs
        // None.
    }

    get estimatedClosingCosts() {
        return this.#estimatedClosingCosts;
    }

    set buyerAcquisitionCost(value: number) {
        this.#buyerAcquisitionCost = value;

        // Update derived Outputs
        this.secondSideSalePrice = this.calcSecondSideSalePrice(this.annualGrossIncome, this.desiredGrossROIPercent, this.buyerAcquisitionCost);
    }

    get buyerAcquisitionCost() {
        return this.#buyerAcquisitionCost;
    }

    set managementFee(value: number) {
        this.#managementFee = value;

        // Update derived Outputs
        // None.
    }

    get managementFee() {
        return this.#managementFee;
    }

    set frontSideCommissionEarned(value: number) {
        this.#frontSideCommissionEarned = value;

        // Update derived Outputs
        this.totalSBSpread = this.calcTotalSBSpread(this.wholeSaleMarkup, this.frontSideCommissionEarned);
    }

    get frontSideCommissionEarned() {
        return this.#frontSideCommissionEarned;
    }

    set totalSBSpread(value: number) {
        this.#totalSBSpread = value;

        // Update derived Outputs
        // None.
    }

    get totalSBSpread() {
        return this.#totalSBSpread;
    }

    set secondSideSalePrice(value: number) {
        this.#secondSideSalePrice = value;

        // Update derived Outputs
        this.offerPrice = this.calcOfferPrice(this.secondSideSalePrice, this.wholeSaleMarkup);
    }

    get secondSideSalePrice() {
        return this.#secondSideSalePrice;
    }

    set offerPrice(value: number) {
        this.#offerPrice = value;

        // Update derived Outputs
        this.frontSideCommissionEarned = this.calcFrontSideCommissionEarned(this.commissionForSellingAgentPercent, this.offerPrice);
    }

    get offerPrice() {
        return this.#offerPrice;
    }

    init() {
        this.calculator.setFieldValuesFromParseObject([this.state.offerCalculator_rentalCalculationBox, this.state.offerCalculator_rentalOfferCalculatorBox], this.state.offerRental);
    }

    calculate() {
        const state = this.state;

        // Send updated inputs to calculator
        this.arv = this.calculator.getField(state.offerCalculator_rentalCalculationBox, 'arv').inputValue as number;
        this.monthlyRent = this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'rental-rate').inputValue as number;
        this.desiredGrossROIPercent = this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'gross-roi').inputValue as number;
        this.closingCostPercent = this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'closing-cost').inputValue as number;
        this.estimatedRenovationCost = this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'reno-cost').inputValue as number;
        this.managementFeePercent = this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'man-fee').inputValue as number;
        this.commissionForSellingAgentPercent = this.calculator.getField(state.offerCalculator_rentalCalculationBox, 'commission').inputValue as number;
        this.wholeSaleMarkup = this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'markup').inputValue as number;

        // Reflect calculation outputs in form
        this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'gross-income').outputValue = this.annualGrossIncome;
        this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'closing-cost').outputValue = this.estimatedClosingCosts;
        this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'acqui-cost').outputValue = this.buyerAcquisitionCost;
        this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'man-fee').outputValue = this.managementFee;
        this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'front-comission').outputValue = this.frontSideCommissionEarned;
        this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'sb-spread').outputValue = this.totalSBSpread;
        this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'second-sale-price').outputValue = this.secondSideSalePrice;
        this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'offer-price').outputValue = this.offerPrice;

        if (!this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'insurance').inputValue) {
            // If no insurance value is defined then use estimate
            this.calculator.getField(state.offerCalculator_rentalOfferCalculatorBox, 'insurance').formControl?.setValue(this.currencyPipe.transform(this.insuranceEstimateAnnual));
        }

        // Save offerPrice on Parse for display elsewhere
        this.state.offerRental.set('offerPrice', this.offerPrice);
    }

    /**
     * Calculations
     */

    calcAnnualGrossIncome(monthlyRent: number): number {
        return monthlyRent * 12;
    }

    calcEstimatedClosingCosts(annualGrossIncome: number, desiredGrossROIPercent: number, closingCostPercent: number): number {
        const temp = annualGrossIncome / desiredGrossROIPercent;
        return temp * closingCostPercent;
    }

    calcBuyerAcquisitionCost(estimatedRenovationCost: number, estimatedClosingCost: number): number {
        return estimatedRenovationCost + estimatedClosingCost;
    }

    calcManagementFee(annualGrossIncome: number, managementFeePercent: number): number {
        return annualGrossIncome * managementFeePercent;
    }

    calcFrontSideCommissionEarned(commissionForSellingAgentPercent: number, offerPrice: number): number {
        return offerPrice * commissionForSellingAgentPercent;
    }

    calcTotalSBSpread(wholeSaleMarkup: number, frontSideCommissionEarned: number): number {
        return wholeSaleMarkup + frontSideCommissionEarned;
    }

    calcSecondSideSalePrice(annualGrossIncome: number, desiredGrossROIPercent: number, buyerAcquisitionCost: number): number {
        return (annualGrossIncome / desiredGrossROIPercent) - buyerAcquisitionCost;
    }

    calcOfferPrice(secondSideSalePrice: number, wholeSaleMarkup: number): number {
        return secondSideSalePrice - wholeSaleMarkup;
    }
}
